<template>
  <div :class="renderVariant" class="ls-provider-info-card--container">
    <div v-if="!showingDetails && renderVariant === 'default'" class="view-details">
      <a href="#" @click.prevent="showingDetails = true">Show&nbsp;Profile</a>
      <a v-if="renderDecline" href="#" @click.prevent="openLeadsDeclineModal">Decline&nbsp;Pro</a>
    </div>
    <div
      v-if="showingDetails || renderVariant === 'onboarding'"
      class="ls-provider-info-card--pills--details"
    >
      <div class="ls-provider-info-card--pills">
        <div v-for="pill in pillsEligibleForRender" :key="pill" class="ls-provider-info-card--pill">
          <LsPill :bg-color="getPillColor(pill)" :label="pill" />
        </div>
      </div>
      <div class="ls-provider-info-card--heading">Highlights</div>
      <div class="ls-provider-info-card--stat-list">
        <div class="ls-provider-info-card--stat-item">
          <LsIcon type="calendar" size="sm" />
          <p>
            {{ contractor.years_of_service }} year{{
              contractor.years_of_service === 1 ? '' : 's'
            }}
            of service
          </p>
        </div>
        <div v-if="responseTimeString" class="ls-provider-info-card--stat-item">
          <LsIcon type="clock" size="sm" />
          <p>{{ responseTimeString }}</p>
        </div>
        <div class="ls-provider-info-card--stat-item">
          <LsIcon type="staroutline" size="sm" />
          <p>{{ contractor.metrics.ratings.average }} of 5 average rating</p>
        </div>
        <div
          v-if="contractor.metrics.customer_leads_hires.lifetime > 0"
          class="ls-provider-info-card--stat-item"
        >
          <LsIcon type="check" size="sm" />
          <p>
            {{ contractor.metrics.customer_leads_hires.lifetime }} hire{{
              contractor.metrics.customer_leads_hires.lifetime === 1 ? '' : 's'
            }}
            on {{ brandName }}
          </p>
        </div>
      </div>
      <div v-if="showBio && renderVariant === 'default'" class="ls-provider-info-card--bio">
        <div class="ls-provider-info-card--heading">About Me:</div>
        <p>{{ contractor.bio }}</p>
      </div>
      <div
        v-if="showBio && renderVariant === 'onboarding'"
        class="ls-provider-info-card--bio-onboarding"
      >
        <div class="ls-provider-info-card--heading">About Me</div>
        <p
          :class="[
            showingDetails
              ? 'ls-provider-info-card--bio-showing-details'
              : 'ls-provider-info-card--bio-showing-less',
          ]"
        >
          {{ contractor.bio }}
        </p>
        <div v-if="showBio && renderVariant === 'onboarding'" class="view-details">
          <a v-if="!showingDetails" href="#" @click.prevent="showingDetails = true"
            >View&nbsp;Details</a
          >
          <a v-if="showingDetails" href="#" @click.prevent="showingDetails = false"
            >View&nbsp;Less</a
          >
        </div>
      </div>
      <div
        v-if="showingDetails && renderVariant === 'default'"
        class="ls-provider-info-card--view-less"
      >
        <a href="#" @click.prevent="showingDetails = false">View&nbsp;Less</a>
        <a v-if="renderDecline" href="#" @click.prevent="openLeadsDeclineModal">Decline&nbsp;Pro</a>
      </div>
    </div>
  </div>
</template>

<script>
import VueTypes from 'vue-types';
import _ from 'lodash';
import { mapState } from 'vuex';
import { lsPropTypeHelpers, lsModalService } from 'ls-vue-common';
import { LsIcon, LsPill } from '../common';
import { LeadsDeclineModal } from '@/modals';
import { themedComponentMixin } from '@/mixins';

const pillColors = {
  'top performer': 'beige',
  'in high demand': 'primary-light',
  'fast response': 'blue',
};

export default {
  name: 'LsProviderInfoCardDetails',
  components: { LsIcon, LsPill },
  mixins: [themedComponentMixin],
  props: {
    id: VueTypes.number,
    contractor: VueTypes.shape({
      bio: VueTypes.custom(lsPropTypeHelpers.stringOrNull),
      user: VueTypes.shape({
        first_name: VueTypes.string,
        last_name: VueTypes.string,
        profile_image_url: VueTypes.custom(lsPropTypeHelpers.stringOrNull),
      }).isRequired.loose,
      metrics: VueTypes.shape({
        ratings: VueTypes.shape({
          average: VueTypes.number.isRequired,
          is_top_performer: VueTypes.bool,
        }).isRequired.loose,
        response_time: VueTypes.shape({
          average: VueTypes.number.isRequired,
          is_fast: VueTypes.bool.isRequired,
        }).loose,
        customer_leads_hires: VueTypes.shape({
          lifetime: VueTypes.number.isRequired,
          is_high_demand: VueTypes.bool.isRequired,
        }).loose,
      }).isRequired,
      name: VueTypes.string,
      city: VueTypes.string,
      state: VueTypes.string,
      years_of_service: VueTypes.number,
    }).isRequired.loose,
    property: VueTypes.shape({
      id: VueTypes.number,
    }).loose,
  },
  data() {
    return {
      showingDetails: false,
    };
  },
  computed: {
    ...mapState(['featureFlags', 'screenSize']),
    pillsEligibleForRender() {
      const { metrics } = this.contractor;
      const pillLabels = [];
      if (metrics.ratings.is_top_performer) {
        pillLabels.push('Top Performer');
      }
      if (metrics.response_time.is_fast) {
        pillLabels.push('Fast Response');
      }
      if (metrics.customer_leads_hires.is_high_demand) {
        pillLabels.push('In High Demand');
      }

      return pillLabels;
    },
    responseTimeString() {
      let numDays = this.contractor.metrics.response_time.average / 1440;
      const responseString = 'avg response time';
      if (!isNaN(numDays)) {
        numDays = Math.round(numDays);
      }
      if (!numDays) {
        return null;
      }
      return numDays !== 1
        ? `${numDays} days ${responseString}`
        : `${numDays} day ${responseString}`;
    },
    showBio() {
      return this.contractor.bio && this.contractor.bio.length >= 200;
    },
    renderVariant() {
      return _.get(this.$router, 'currentRoute.matched[0].meta.isOnboarding', false)
        ? 'onboarding'
        : 'default';
    },
    renderDecline() {
      return (
        this.featureFlags.flags['customer.customerLeads.contact.decline'] &&
        _.get(this.property, 'id') &&
        _.size(
          _.filter(this.property.customer_leads[0].customer_lead_contacts, ({ status }) => {
            return status !== 'closed';
          })
        ) > 1
      );
    },
  },
  methods: {
    getPillColor(label) {
      return pillColors[label.toLowerCase()];
    },
    openLeadsDeclineModal() {
      lsModalService.open({
        parent: this,
        component: LeadsDeclineModal,
        props: {
          propertyId: this.property.id,
          customerLeadId: this.property.customer_leads[0].id,
          contactId: this.id,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.ls-provider-info-card--container {
  display: flex;
  align-items: flex-start;
  .ls-provider-info-card--avatar {
    display: flex;
    align-self: flex-start;
    flex-wrap: wrap;
    margin-right: 10px;
    width: 80px;
    height: 80px;
    img {
      min-width: 80px;
      min-height: 60px;
      width: 80px;
    }
  }
  .ls-provider-info-card--bio {
    display: inline;
    p {
      display: inline;
    }
    a {
      display: inline;
    }
  }
  .view-details,
  .ls-provider-info-card--view-less {
    color: $primary;
    margin-left: 5px;
    a {
      color: $primary;
      text-decoration: underline;
    }

    a:nth-child(2) {
      color: $gray-primary;
      margin-left: 10px;
      border-left: 1px solid $primary-light;
      padding-left: 10px;
    }
  }
  .view-details {
    margin-top: 10px;
  }
  .ls-provider-info-card--name {
    font-weight: 700;
    font-size: 14px;
    max-width: 500px;
    margin-bottom: 10px;
    @media (max-width: $mobile-max-width) {
      font-size: 12px;
      max-width: 320px;
    }
  }
  .ls-provider-info-card--pills {
    display: flex;
    align-items: center;
  }

  .ls-provider-info-card--pill {
    margin: 5px 5px 0 0;
    @media (max-width: $mobile-max-width) {
      margin-top: 10px;
    }
  }

  .ls-provider-info-card--bio-showing-details,
  .ls-provider-info-card--bio-showing-less {
    line-height: 1.5em;
    max-width: 500px;
    @media (max-width: $mobile-max-width) {
      max-width: 320px;
    }
  }

  .ls-provider-info-card--bio-showing-less {
    height: 3em;
    overflow: hidden;
    @media (max-width: $mobile-max-width) {
      height: 1.5em;
    }
  }
}

.ls-provider-info-card--heading {
  font-weight: 700;
  font-size: 12px;
  margin: 10px 0 10px;
}

.ls-provider-info-card--stat-list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  max-width: 400px;
}

.ls-provider-info-card--stat-item {
  display: flex;
  align-items: center;
  width: 50%;
  margin-bottom: 5px;
  svg {
    margin-right: 10px;
  }
  @media (max-width: $mobile-max-width) {
    width: 100%;
  }
}

// Specific to onboarding routes
.onboarding .ls-provider-info-card--bio-showing-details {
  max-width: 500px;
  @media (max-width: $mobile-max-width) {
    max-width: 320px;
  }
}

.onboarding .view-details {
  margin-left: 0;
}

// all routes other than onboarding
.default .ls-provider-info-card--details-wrapper {
  max-width: 340px;
  @media (max-width: $mobile-max-width) {
    max-width: 320px;
  }
}
.themed-app {
  .ls-provider-info-card--container {
    .ls-provider-info-card--view-less {
      color: $theme-color-primary;
      a {
        color: $theme-color-primary;
      }
      a:nth-child(2) {
        color: $theme-color-gray-darker;
        border-left: 1px solid $theme-color-primary-light;
      }
    }
  }
}
</style>
